import { Fragment, Slice } from '@atlaskit/editor-prosemirror/model';
import {
	AddMarkStep,
	RemoveMarkStep,
	ReplaceStep,
	type Step,
} from '@atlaskit/editor-prosemirror/transform';

import { marksAtPosition, textNodesFromRun } from './coalesce';
import { diffList } from './diff-list';
import { clipSpans, findMarkDeltas } from './marks';
import type { DiffContext, MarkSpan, TextRun } from './types';
import { filterMarks } from './utils';

/**
 * Diffs two TextRuns (coalesced inline text + mark spans) and emits PM steps in
 * old-doc coordinates. `parentPos` is the PM position of the first character of
 * the OLD text run.
 *
 * Ignored marks are handled in two places:
 *  - Match: spans are filtered before mark-delta computation, so ignored marks
 *    never become Add/Remove steps. The original (un-filtered) marks remain on
 *    the surviving text via PM's standard mark mapping.
 *  - Insert / Replace: ignored marks from the OLD position are inherited into
 *    the emitted slice, so an annotation on text the user edits ends up on
 *    the replacement text rather than getting lost when its anchor text dies.
 */
export function diffText(
	oldRun: TextRun,
	newRun: TextRun,
	parentPos: number,
	ctx: DiffContext,
): Step[] {
	const schema = ctx.schema;
	const ignore = ctx.ignoreMarks;
	const steps: Step[] = [];

	if (oldRun.text.length === 0 && newRun.text.length === 0) {
		return [];
	}
	if (oldRun.text.length === 0) {
		const nodes = textNodesFromRun(newRun, 0, newRun.text.length, schema.text.bind(schema));
		steps.push(new ReplaceStep(parentPos, parentPos, new Slice(Fragment.from(nodes), 0, 0)));
		return steps;
	}
	if (newRun.text.length === 0) {
		steps.push(new ReplaceStep(parentPos, parentPos + oldRun.text.length, Slice.empty));
		return steps;
	}

	diffList<string>(
		Array.from(oldRun.text),
		Array.from(newRun.text),
		(op) => {
			switch (op.kind) {
				case 'match': {
					const oldClipped = filteredClipped(
						oldRun.spans,
						op.oldIndex,
						op.oldIndex + op.length,
						op.oldIndex,
						ignore,
					);
					const newClipped = filteredClipped(
						newRun.spans,
						op.newIndex,
						op.newIndex + op.length,
						op.newIndex,
						ignore,
					);
					for (const d of findMarkDeltas(oldClipped, newClipped)) {
						const from = parentPos + op.oldIndex + d.from;
						const to = parentPos + op.oldIndex + d.to;
						steps.push(
							d.type === 'insert'
								? new AddMarkStep(from, to, d.mark)
								: new RemoveMarkStep(from, to, d.mark),
						);
					}
					return;
				}
				case 'insert': {
					const insertPos = parentPos + op.insertAt;
					// Inherit ignored marks from the char just before the insertion point
					// (or position 0 if we're at the start of the run).
					const inheritFrom = Math.max(0, op.insertAt - 1);
					const inherited = marksAtPosition(oldRun, inheritFrom, ignore);
					const nodes = textNodesFromRun(
						newRun,
						op.from,
						op.to,
						schema.text.bind(schema),
						inherited,
					);
					steps.push(new ReplaceStep(insertPos, insertPos, new Slice(Fragment.from(nodes), 0, 0)));
					return;
				}
				case 'delete': {
					steps.push(new ReplaceStep(parentPos + op.from, parentPos + op.to, Slice.empty));
					return;
				}
				case 'replace': {
					// Inherit ignored marks from the start of the replaced range.
					// (Crossing an annotation boundary mid-range is an inherent
					// ambiguity; the start-position rule keeps the typical "edit text
					// inside one annotation" case correct.)
					const inherited = marksAtPosition(oldRun, op.replaceFrom, ignore);
					const nodes = textNodesFromRun(
						newRun,
						op.from,
						op.to,
						schema.text.bind(schema),
						inherited,
					);
					steps.push(
						new ReplaceStep(
							parentPos + op.replaceFrom,
							parentPos + op.replaceTo,
							new Slice(Fragment.from(nodes), 0, 0),
						),
					);
					return;
				}
			}
		},
		(a, b) => a === b,
		ctx.maxTextRegions,
	);

	return steps;
}

/**
 * Clips spans to `[from, to)`, normalises them to range-local coordinates
 * (subtracting `origin`), and strips marks whose type is in `ignore`. Used by
 * the Match handler so mark-delta computation runs on the visible mark set
 * only.
 */
function filteredClipped(
	spans: readonly MarkSpan[],
	from: number,
	to: number,
	origin: number,
	ignore: ReadonlySet<string>,
): MarkSpan[] {
	return clipSpans(spans, from, to).map((s) => ({
		start: s.start - origin,
		end: s.end - origin,
		marks: filterMarks(s.marks, ignore),
	}));
}
